import { t, tOrFallback } from '../i18n';
import { ActionTooltip } from './ActionTooltip';
import {
  BINDABLE_ACTIONS,
  DEFAULT_BINDINGS,
  getActionLabelKey,
  normalizeKey,
  type BindableAction,
  type KeyBindingsManager,
} from '../settings/KeyBindings';

interface KeyBindingsPanel {
  element: HTMLDivElement;
  show(): void;
  hide(): void;
}

interface KeyBindingsPanelOptions {
  onShow?: () => void;
  onHide?: () => void;
}

interface KeyDef {
  label: string;
  eventKey: string;
  w: number;
}

type KBRow = (KeyDef | null)[];

function fallbackActionLabel(action: BindableAction): string {
  const trimmed = action.startsWith('toggle') ? action.slice('toggle'.length) : action;
  return trimmed
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
}

function getActionLabel(action: BindableAction): string {
  return tOrFallback(getActionLabelKey(action), fallbackActionLabel(action));
}

// Main keyboard section (Alphanumeric + Function keys)
const MAIN_LAYOUT: KBRow[] = [
  // Function keys
  [
    { label: 'Esc', eventKey: 'Escape', w: 1.0 },
    null,
    { label: 'F1', eventKey: 'F1', w: 1.0 },
    { label: 'F2', eventKey: 'F2', w: 1.0 },
    { label: 'F3', eventKey: 'F3', w: 1.0 },
    { label: 'F4', eventKey: 'F4', w: 1.0 },
    null,
    { label: 'F5', eventKey: 'F5', w: 1.0 },
    { label: 'F6', eventKey: 'F6', w: 1.0 },
    { label: 'F7', eventKey: 'F7', w: 1.0 },
    { label: 'F8', eventKey: 'F8', w: 1.0 },
    null,
    { label: 'F9', eventKey: 'F9', w: 1.0 },
    { label: 'F10', eventKey: 'F10', w: 1.0 },
    { label: 'F11', eventKey: 'F11', w: 1.0 },
    { label: 'F12', eventKey: 'F12', w: 1.0 },
  ],
  // Number row
  [
    { label: '`', eventKey: '`', w: 1.0 },
    { label: '1', eventKey: '1', w: 1.0 },
    { label: '2', eventKey: '2', w: 1.0 },
    { label: '3', eventKey: '3', w: 1.0 },
    { label: '4', eventKey: '4', w: 1.0 },
    { label: '5', eventKey: '5', w: 1.0 },
    { label: '6', eventKey: '6', w: 1.0 },
    { label: '7', eventKey: '7', w: 1.0 },
    { label: '8', eventKey: '8', w: 1.0 },
    { label: '9', eventKey: '9', w: 1.0 },
    { label: '0', eventKey: '0', w: 1.0 },
    { label: '-', eventKey: '-', w: 1.0 },
    { label: '=', eventKey: '=', w: 1.0 },
    { label: 'Back', eventKey: 'Backspace', w: 2.0 },
  ],
  // Top letter row
  [
    { label: 'Tab', eventKey: 'Tab', w: 1.5 },
    { label: 'Q', eventKey: 'q', w: 1.0 },
    { label: 'W', eventKey: 'w', w: 1.0 },
    { label: 'E', eventKey: 'e', w: 1.0 },
    { label: 'R', eventKey: 'r', w: 1.0 },
    { label: 'T', eventKey: 't', w: 1.0 },
    { label: 'Y', eventKey: 'y', w: 1.0 },
    { label: 'U', eventKey: 'u', w: 1.0 },
    { label: 'I', eventKey: 'i', w: 1.0 },
    { label: 'O', eventKey: 'o', w: 1.0 },
    { label: 'P', eventKey: 'p', w: 1.0 },
    { label: '[', eventKey: '[', w: 1.0 },
    { label: ']', eventKey: ']', w: 1.0 },
    { label: '\\', eventKey: '\\', w: 1.5 },
  ],
  // Home row
  [
    { label: 'Caps', eventKey: 'CapsLock', w: 1.75 },
    { label: 'A', eventKey: 'a', w: 1.0 },
    { label: 'S', eventKey: 's', w: 1.0 },
    { label: 'D', eventKey: 'd', w: 1.0 },
    { label: 'F', eventKey: 'f', w: 1.0 },
    { label: 'G', eventKey: 'g', w: 1.0 },
    { label: 'H', eventKey: 'h', w: 1.0 },
    { label: 'J', eventKey: 'j', w: 1.0 },
    { label: 'K', eventKey: 'k', w: 1.0 },
    { label: 'L', eventKey: 'l', w: 1.0 },
    { label: ';', eventKey: ';', w: 1.0 },
    { label: '\'', eventKey: '\'', w: 1.0 },
    { label: 'Enter', eventKey: 'Enter', w: 2.25 },
  ],
  // Bottom letter row
  [
    { label: 'Shift', eventKey: 'Shift', w: 2.25 },
    { label: 'Z', eventKey: 'z', w: 1.0 },
    { label: 'X', eventKey: 'x', w: 1.0 },
    { label: 'C', eventKey: 'c', w: 1.0 },
    { label: 'V', eventKey: 'v', w: 1.0 },
    { label: 'B', eventKey: 'b', w: 1.0 },
    { label: 'N', eventKey: 'n', w: 1.0 },
    { label: 'M', eventKey: 'm', w: 1.0 },
    { label: ',', eventKey: ',', w: 1.0 },
    { label: '.', eventKey: '.', w: 1.0 },
    { label: '/', eventKey: '/', w: 1.0 },
    { label: 'Shift', eventKey: 'Shift', w: 2.75 },
  ],
  // Control row
  [
    { label: 'Ctrl', eventKey: 'Control', w: 1.5 },
    { label: 'Alt', eventKey: 'Alt', w: 1.25 },
    { label: 'Space', eventKey: ' ', w: 6.25 },
    { label: 'Alt', eventKey: 'Alt', w: 1.25 },
    { label: 'Ctrl', eventKey: 'Control', w: 1.5 },
  ],
];

// Special/Control keys block
const MIDDLE_LAYOUT: KBRow[] = [
  // Print Screen, Scroll Lock, Pause
  [
    { label: 'PrtSc', eventKey: 'PrintScreen', w: 1.0 },
    { label: 'ScrlLk', eventKey: 'ScrollLock', w: 1.0 },
    { label: 'Pause', eventKey: 'Pause', w: 1.0 },
  ],
  // Spacing between row sets
  [null],
  // Insert, Home, PgUp
  [
    { label: 'Ins', eventKey: 'Insert', w: 1.0 },
    { label: 'Home', eventKey: 'Home', w: 1.0 },
    { label: 'PgUp', eventKey: 'PageUp', w: 1.0 },
  ],
  // Delete, End, PgDn
  [
    { label: 'Del', eventKey: 'Delete', w: 1.0 },
    { label: 'End', eventKey: 'End', w: 1.0 },
    { label: 'PgDn', eventKey: 'PageDown', w: 1.0 },
  ],
  // Spacing before arrows
  [null],
  // Arrow keys (handled separately for custom alignment)
];

// Numeric keypad block
const NUMPAD_LAYOUT: KBRow[] = [
  // NumLock, /, *, -
  [
    { label: 'Num', eventKey: 'NumLock', w: 1.0 },
    { label: '/', eventKey: '/', w: 1.0 },
    { label: '*', eventKey: '*', w: 1.0 },
    { label: '-', eventKey: '-', w: 1.0 },
  ],
  // 7, 8, 9, (half of +)
  [
    { label: '7', eventKey: '7', w: 1.0 },
    { label: '8', eventKey: '8', w: 1.0 },
    { label: '9', eventKey: '9', w: 1.0 },
    { label: '+', eventKey: '+', w: 1.0 },
  ],
  // 4, 5, 6
  [
    { label: '4', eventKey: '4', w: 1.0 },
    { label: '5', eventKey: '5', w: 1.0 },
    { label: '6', eventKey: '6', w: 1.0 },
    null, // Placeholder for the rest of +
  ],
  // 1, 2, 3, (half of Enter)
  [
    { label: '1', eventKey: '1', w: 1.0 },
    { label: '2', eventKey: '2', w: 1.0 },
    { label: '3', eventKey: '3', w: 1.0 },
    { label: 'Ent', eventKey: 'Enter', w: 1.0 },
  ],
  // 0, ., (half of Enter)
  [
    { label: '0', eventKey: '0', w: 2.0 },
    { label: '.', eventKey: '.', w: 1.0 },
    null, // Placeholder for the rest of Enter
  ],
];

function isReservedEventKey(eventKey: string): boolean {
  return eventKey === 'Escape' || /^[1-9]$/.test(eventKey);
}

function cloneBindings(source: Record<BindableAction, string>): Record<BindableAction, string> {
  return { ...source };
}

export function createKeyBindingsPanel(
  keyBindings: KeyBindingsManager,
  options: KeyBindingsPanelOptions = {}
): KeyBindingsPanel {
  let visible = false;
  let pendingBindings = cloneBindings(keyBindings.getBindings());
  let draggedAction: BindableAction | null = null;
  let dropHandled = false;
  const tooltip = new ActionTooltip();

  const element = document.createElement('div');
  element.id = 'key-bindings-modal';
  Object.assign(element.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '6000',
    background: 'rgba(2, 6, 23, 0.85)',
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  });

  const panel = document.createElement('div');
  panel.id = 'key-bindings-panel';
  Object.assign(panel.style, {
    width: 'min(1280px, 98vw)',
    padding: '24px',
    borderRadius: '12px',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    background: '#0f172a',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    userSelect: 'none',
  });

  const title = document.createElement('h3');
  title.textContent = t('keyBindings.title');
  Object.assign(title.style, {
    margin: '0',
    fontSize: '1.25rem',
    color: '#f8fafc',
    textAlign: 'center',
  });

  const keyboardScrollContainer = document.createElement('div');
  Object.assign(keyboardScrollContainer.style, {
    overflowX: 'auto',
    display: 'flex',
    justifyContent: 'center',
    padding: '10px 0',
  });

  const keyboardContainer = document.createElement('div');
  keyboardContainer.className = 'keyboard-container';
  Object.assign(keyboardContainer.style, {
    display: 'flex',
    flexDirection: 'row',
    gap: '30px',
    justifyContent: 'center',
    minWidth: 'fit-content',
  });

  const mainSection = document.createElement('div');
  Object.assign(mainSection.style, { display: 'flex', flexDirection: 'column', gap: '3px' });

  const middleSection = document.createElement('div');
  Object.assign(middleSection.style, { display: 'flex', flexDirection: 'column', gap: '3px' });

  const numpadSection = document.createElement('div');
  Object.assign(numpadSection.style, { display: 'flex', flexDirection: 'column', gap: '3px' });

  const poolContainer = document.createElement('div');
  poolContainer.className = 'action-pool';
  Object.assign(poolContainer.style, {
    minHeight: '80px',
    padding: '16px',
    borderRadius: '8px',
    background: 'rgba(15, 23, 42, 0.4)',
    border: '2px dashed rgba(148, 163, 184, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  });

  const poolHeader = document.createElement('div');
  Object.assign(poolHeader.style, {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  });

  const poolTitle = document.createElement('span');
  poolTitle.textContent = t('keyBindings.unboundActions');
  Object.assign(poolTitle.style, {
    fontSize: '0.85rem',
    color: '#94a3b8',
    fontWeight: '600',
    textTransform: 'uppercase',
  });

  const poolHint = document.createElement('span');
  poolHint.textContent = t('keyBindings.dragHint');
  Object.assign(poolHint.style, {
    fontSize: '0.75rem',
    color: '#64748b',
    fontStyle: 'italic',
  });

  poolHeader.appendChild(poolTitle);
  poolHeader.appendChild(poolHint);
  poolContainer.appendChild(poolHeader);

  const poolChips = document.createElement('div');
  Object.assign(poolChips.style, {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  });
  poolContainer.appendChild(poolChips);

  const buttonBar = document.createElement('div');
  Object.assign(buttonBar.style, {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '10px',
  });

  const applyButton = document.createElement('button');
  applyButton.textContent = t('keyBindings.apply');
  const cancelButton = document.createElement('button');
  cancelButton.textContent = t('keyBindings.cancel');
  const resetButton = document.createElement('button');
  resetButton.textContent = t('keyBindings.reset');

  const styleBtn = (btn: HTMLButtonElement, primary = false) => {
    Object.assign(btn.style, {
      padding: '8px 24px',
      borderRadius: '6px',
      border: primary ? '1px solid #38bdf8' : '1px solid rgba(148, 163, 184, 0.3)',
      background: primary ? 'rgba(14, 165, 233, 0.15)' : 'transparent',
      color: primary ? '#38bdf8' : '#e2e8f0',
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    });
    btn.addEventListener('mouseenter', () => {
      btn.style.background = primary ? 'rgba(14, 165, 233, 0.25)' : 'rgba(148, 163, 184, 0.1)';
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = primary ? 'rgba(14, 165, 233, 0.15)' : 'transparent';
    });
  };

  styleBtn(resetButton);
  styleBtn(cancelButton);
  styleBtn(applyButton, true);

  buttonBar.appendChild(resetButton);
  buttonBar.appendChild(cancelButton);
  buttonBar.appendChild(applyButton);

  panel.appendChild(title);
  keyboardContainer.appendChild(mainSection);
  keyboardContainer.appendChild(middleSection);
  keyboardContainer.appendChild(numpadSection);
  keyboardScrollContainer.appendChild(keyboardContainer);
  panel.appendChild(keyboardScrollContainer);
  panel.appendChild(poolContainer);
  panel.appendChild(buttonBar);
  element.appendChild(panel);

  const findActionForKey = (eventKey: string): BindableAction | null => {
    const normalized = normalizeKey(eventKey);
    for (const action of BINDABLE_ACTIONS) {
      if (normalizeKey(pendingBindings[action]) === normalized) {
        return action;
      }
    }
    return null;
  };

  const KEY_UNIT = 40;
  const KEY_GAP = 3;

  const render = () => {
    renderMain();
    renderMiddle();
    renderNumpad();
    renderPool();
  };

  const renderLayout = (layout: KBRow[], container: HTMLDivElement) => {
    container.innerHTML = '';
    layout.forEach((row) => {
      const rowDiv = document.createElement('div');
      Object.assign(rowDiv.style, {
        display: 'flex',
        gap: `${KEY_GAP}px`,
      });

      row.forEach((key, idx) => {
        if (key === null) {
          const spacer = document.createElement('div');
          // Heuristic for spacing: if it's the first row of MAIN_LAYOUT, it's a half-unit spacer for F-keys
          if (row === MAIN_LAYOUT[0]) {
            spacer.style.width = `${KEY_UNIT / 2}px`;
          } else {
            // Default 1-unit spacer for alignment or vertical padding rows
            spacer.style.width = `${KEY_UNIT}px`;
            spacer.style.height = `${KEY_UNIT}px`;
            spacer.style.flexShrink = '0';
          }
          rowDiv.appendChild(spacer);
        } else {
          rowDiv.appendChild(createKeyDiv(key));
        }
      });
      container.appendChild(rowDiv);
    });
  };

  const renderMain = () => renderLayout(MAIN_LAYOUT, mainSection);

  const renderMiddle = () => {
    renderLayout(MIDDLE_LAYOUT, middleSection);

    // Custom Arrow Cluster at bottom of middle section
    const upRow = document.createElement('div');
    Object.assign(upRow.style, { display: 'flex', justifyContent: 'center', marginTop: '10px' });
    upRow.appendChild(createKeyDiv({ label: '▲', eventKey: 'ArrowUp', w: 1 }));
    middleSection.appendChild(upRow);

    const bottomRow = document.createElement('div');
    Object.assign(bottomRow.style, { display: 'flex', gap: `${KEY_GAP}px`, justifyContent: 'center' });
    bottomRow.appendChild(createKeyDiv({ label: '◄', eventKey: 'ArrowLeft', w: 1 }));
    bottomRow.appendChild(createKeyDiv({ label: '▼', eventKey: 'ArrowDown', w: 1 }));
    bottomRow.appendChild(createKeyDiv({ label: '►', eventKey: 'ArrowRight', w: 1 }));
    middleSection.appendChild(bottomRow);
  };

  const renderNumpad = () => renderLayout(NUMPAD_LAYOUT, numpadSection);

  const createKeyDiv = (key: KeyDef): HTMLDivElement => {
    const div = document.createElement('div');
    const action = findActionForKey(key.eventKey);
    const reserved = isReservedEventKey(key.eventKey);

    const width = KEY_UNIT * key.w + KEY_GAP * (key.w - 1);
    Object.assign(div.style, {
      width: `${width}px`,
      height: `${KEY_UNIT}px`,
      borderRadius: '4px',
      background: reserved ? 'rgba(30, 30, 40, 0.4)' : 'rgba(51, 65, 85, 0.6)',
      border: action ? '1px solid rgba(56, 189, 248, 0.7)' : '1px solid rgba(148, 163, 184, 0.3)',
      display: 'flex',
      flexDirection: 'column',
      padding: '4px',
      boxSizing: 'border-box',
      position: 'relative',
      opacity: reserved ? '0.5' : '1',
      transition: 'all 0.15s',
    });

    const capLabel = document.createElement('span');
    capLabel.textContent = key.label;
    Object.assign(capLabel.style, {
      fontSize: '0.55rem',
      color: '#94a3b8',
      pointerEvents: 'none',
    });
    div.appendChild(capLabel);

    if (action) {
      const actionLabelText = getActionLabel(action);
      const actionLabel = document.createElement('span');
      actionLabel.textContent = actionLabelText;
      Object.assign(actionLabel.style, {
        fontSize: '0.5rem',
        color: '#5ad1fa',
        marginTop: 'auto',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        pointerEvents: 'none',
      });
      div.appendChild(actionLabel);

      if (!reserved) {
        div.draggable = true;
        div.style.cursor = 'grab';
        div.addEventListener('dragstart', (e) => onDragStart(e, action));
        div.addEventListener('dragend', onDragEnd);
      }

      div.addEventListener('mouseenter', (event) => {
        tooltip.show({ title: actionLabelText }, event.clientX, event.clientY);
      });
      div.addEventListener('mousemove', (event) => {
        tooltip.move(event.clientX, event.clientY);
      });
      div.addEventListener('mouseleave', () => {
        tooltip.hide();
      });
      div.addEventListener('mousedown', () => {
        tooltip.hide();
      });
    }

    if (!reserved) {
      div.addEventListener('dragover', (e) => {
        e.preventDefault();
        div.style.background = 'rgba(37, 99, 235, 0.35)';
        div.style.borderColor = '#60a5fa';
      });
      div.addEventListener('dragleave', () => {
        div.style.background = 'rgba(51, 65, 85, 0.6)';
        div.style.borderColor = action ? 'rgba(56, 189, 248, 0.7)' : 'rgba(148, 163, 184, 0.3)';
      });
      div.addEventListener('drop', (e) => {
        e.preventDefault();
        onDropOnKey(key.eventKey);
      });
    }

    return div;
  };

  const renderPool = () => {
    poolChips.innerHTML = '';
    const unbound = BINDABLE_ACTIONS.filter((a) => pendingBindings[a] === '');

    unbound.forEach((action) => {
      const chip = document.createElement('div');
      chip.textContent = getActionLabel(action);
      Object.assign(chip.style, {
        padding: '4px 10px',
        borderRadius: '4px',
        background: 'rgba(37, 99, 235, 0.3)',
        border: '1px solid rgba(90, 209, 250, 0.5)',
        color: '#dbeafe',
        fontSize: '0.75rem',
        cursor: 'grab',
      });
      chip.draggable = true;
      chip.addEventListener('dragstart', (e) => onDragStart(e, action));
      chip.addEventListener('dragend', onDragEnd);
      poolChips.appendChild(chip);
    });
  };

  const onDragStart = (e: DragEvent, action: BindableAction) => {
    draggedAction = action;
    dropHandled = false;
    if (e.dataTransfer) {
      e.dataTransfer.setData('text/plain', action);
      e.dataTransfer.effectAllowed = 'move';
    }
    (e.target as HTMLElement).style.opacity = '0.5';
  };

  const onDragEnd = (e: DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    if (!dropHandled && draggedAction) {
      pendingBindings[draggedAction] = '';
      render();
    }
    draggedAction = null;
  };

  const onDropOnKey = (eventKey: string) => {
    if (!draggedAction) return;
    const existing = findActionForKey(eventKey);
    if (existing) {
      pendingBindings[existing] = '';
    }
    pendingBindings[draggedAction] = normalizeKey(eventKey);
    dropHandled = true;
    render();
  };

  poolContainer.addEventListener('dragover', (e) => {
    e.preventDefault();
    poolContainer.style.borderColor = '#60a5fa';
    poolContainer.style.background = 'rgba(37, 99, 235, 0.1)';
  });

  poolContainer.addEventListener('dragleave', () => {
    poolContainer.style.borderColor = 'rgba(148, 163, 184, 0.2)';
    poolContainer.style.background = 'rgba(15, 23, 42, 0.4)';
  });

  poolContainer.addEventListener('drop', (e) => {
    e.preventDefault();
    poolContainer.style.borderColor = 'rgba(148, 163, 184, 0.2)';
    poolContainer.style.background = 'rgba(15, 23, 42, 0.4)';
    if (draggedAction) {
      pendingBindings[draggedAction] = '';
      dropHandled = true;
      render();
    }
  });

  const show = (): void => {
    if (visible) return;
    visible = true;
    pendingBindings = cloneBindings(keyBindings.getBindings());
    element.style.display = 'flex';
    options.onShow?.();
    render();
  };

  const hide = (): void => {
    if (!visible) return;
    visible = false;
    element.style.display = 'none';
    tooltip.hide();
    options.onHide?.();
  };

  applyButton.addEventListener('click', () => {
    BINDABLE_ACTIONS.forEach((action) => {
      keyBindings.setBinding(action, pendingBindings[action]);
    });
    keyBindings.save();
    hide();
  });

  cancelButton.addEventListener('click', () => {
    hide();
  });

  resetButton.addEventListener('click', () => {
    pendingBindings = cloneBindings(DEFAULT_BINDINGS);
    render();
  });

  element.addEventListener('mousedown', (e) => {
    if (e.target === element) hide();
  });

  return {
    element,
    show,
    hide,
  };
}
